import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { setTeamMembersLoginEnabled } from 'actionCreators';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import ApiClientManager from 'core/apiClient';
import { useNotification } from 'hooks/useNotification';
import { selectAuthToken } from 'selectors';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { memberQueryKeys } from 'TeamModule/query/membersQueryKeys';
import {
  BulkUpdateMembersSignInStatusResponse,
  MemberSignInStatusResult,
  SIGN_IN_STATUS_BATCH_LIMIT,
  bulkUpdateMembersSignInStatusApi,
} from 'TeamModule/api/bulkUpdateMembersSignInStatusApi';
import type { ActionMenuItem } from 'components/ActionMenu';

/** Members named in the confirmation before it switches to "and N more". */
const NAMED_MEMBERS_LIMIT = 10;

export interface SignInStatusMember {
  teamMembershipId: number;
  name: string;
}

export interface OpenSetSignInStatusModalArgs {
  /** Tenant name for API context - falls back to the global tenant when omitted */
  tenant?: string;
  /** Realm ID for API context - falls back to the global realm when omitted */
  realmId?: string;
  teamId: number;
  members: SignInStatusMember[];
  loginEnabled: boolean;
  /**
   * Patch the organization Members tab's loaded rows in place (that tab reads Redux). The
   * cross-organization page reads React Query and refreshes through the invalidation instead.
   */
  updateLoadedRows?: boolean;
}

const statusLabel = (loginEnabled: boolean) => (loginEnabled ? 'active' : 'inactive');

/**
 * The "Set Sign-in Status" entry of a members bulk menu: Active / Inactive. Leaf items are disabled
 * (and inert) when the selection cannot be acted on, as "Modify Access Level" and the Chello tier do.
 */
export const buildSignInStatusMenuItem = ({
  enabled,
  onSelect,
}: {
  enabled: boolean;
  onSelect: (loginEnabled: boolean) => void;
}): ActionMenuItem => ({
  label: 'Set Sign-in Status',
  subItems: [
    { label: 'Active', loginEnabled: true },
    { label: 'Inactive', loginEnabled: false },
  ].map(({ label, loginEnabled }) => ({
    label,
    disabled: !enabled,
    onClick: enabled ? () => onSelect(loginEnabled) : undefined,
  })),
});

/** "12 set to inactive, 1 blocked: Ana Ruiz (At least one active admin is required.)" */
export const describeSignInStatusResults = (
  results: MemberSignInStatusResult[],
  {
    loginEnabled,
    nameByMembershipId,
  }: { loginEnabled: boolean; nameByMembershipId: Map<number, string> },
): string => {
  const updated = results.filter((result) => result.success).length;
  const blocked = results.filter((result) => !result.success);
  const summary = `${updated} set to ${statusLabel(loginEnabled)}`;
  if (blocked.length === 0) return `${summary}.`;

  const reasons = blocked
    .map((result) => {
      const name = nameByMembershipId.get(result.id) ?? `Account ${result.account_id}`;
      return result.error_message ? `${name} (${result.error_message})` : name;
    })
    .join('; ');
  return `${summary}, ${blocked.length} blocked: ${reasons}`;
};

/** rails' per-member rows, from a 200 body or from the 400 galaxy-server wraps when all were refused. */
const resultsFromError = (error: unknown): MemberSignInStatusResult[] | undefined => {
  const data = (error as any)?.data ?? (error as any)?.response?.data;
  const body = (data?.error ?? data) as Partial<BulkUpdateMembersSignInStatusResponse> | undefined;
  return Array.isArray(body?.team_members) ? body.team_members : undefined;
};

/**
 * Confirm-then-apply modal behind the "Set Sign-in Status" bulk action (GLX-2012). Shared by the
 * organization Members tab and the cross-organization Members page so both send the same request
 * and report the same way.
 */
export const useSetSignInStatusModal = () => {
  const { openModal } = useDynamicModal();
  const token = useSelector(selectAuthToken);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { showSuccess, showWarning, showError } = useNotification();

  const openSetSignInStatusModal = useCallback(
    ({
      tenant,
      realmId,
      teamId,
      members,
      loginEnabled,
      updateLoadedRows = false,
    }: OpenSetSignInStatusModalArgs) => {
      const nameByMembershipId = new Map(members.map((m) => [m.teamMembershipId, m.name]));
      const report = (results: MemberSignInStatusResult[]) => {
        // Rows update in place; the selection is left as it was.
        if (updateLoadedRows) {
          dispatch(
            setTeamMembersLoginEnabled({
              memberships: results
                .filter((result) => result.success)
                .map((result) => ({ teamMembershipId: result.id, accountId: result.account_id })),
              loginEnabled,
            }),
          );
        }
        const message = describeSignInStatusResults(results, { loginEnabled, nameByMembershipId });
        if (results.every((result) => result.success)) showSuccess(message);
        else if (results.some((result) => result.success)) showWarning(message);
        else showError(message);
      };

      const onConfirm = async () => {
        if (!token) return;
        try {
          const { data } = await bulkUpdateMembersSignInStatusApi({
            params: {
              team_id: teamId,
              team_membership_ids: members.map((m) => m.teamMembershipId),
              login_enabled: loginEnabled,
            },
            token,
            config: {
              headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }),
              // The result toast below is the only message, built from rails' per-member rows.
              skipErrorNotification: true,
            },
          });
          report(data.team_members);
        } catch (e) {
          const results = resultsFromError(e);
          if (results) report(results);
          else showError(`Sign-in status update failed: ${extractApiErrorMessage(e)}`);
        } finally {
          // The cross-organization page reads members through React Query. Invalidation only refetches
          // queries on screen, and the impersonation query skips refetch on mount, so drop the off-screen
          // ones: returning to that page then fetches fresh sign-in status instead of the cached one.
          queryClient.removeQueries({ queryKey: memberQueryKeys.all, type: 'inactive' });
          queryClient.invalidateQueries({ queryKey: memberQueryKeys.all });
        }
      };

      const named = members.slice(0, NAMED_MEMBERS_LIMIT).map((m) => m.name);
      const more = members.length - named.length;

      openModal({
        modal: ({ toggle }) => (
          <SimpleModal
            open
            onClose={toggle}
            onConfirm={onConfirm}
            closeOnConfirm
            headerText={`Set ${members.length} member${members.length === 1 ? '' : 's'} to ${loginEnabled ? 'Active' : 'Inactive'}?`}
            confirmButtonText={loginEnabled ? 'Set Active' : 'Set Inactive'}
            onConfirmButtonClassNames={loginEnabled ? '' : 'red-warning-button'}
            Body={
              <div>
                <p>
                  {loginEnabled
                    ? 'They will be able to sign in to Mosaic again.'
                    : 'They will be signed out of Mosaic and will not be able to sign in.'}
                </p>
                <p>
                  {named.join(', ')}
                  {more > 0 ? `, and ${more} more` : ''}
                </p>
              </div>
            }
          />
        ),
      });
    },
    [dispatch, openModal, queryClient, showError, showSuccess, showWarning, token],
  );

  return { openSetSignInStatusModal, batchLimit: SIGN_IN_STATUS_BATCH_LIMIT };
};
